import type { IdentityConfigApiResponse } from '@src/modules/auth/identity-config-api'
import { fetchIdentityConfigByEmail } from '@src/modules/auth/identity-config-api'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

const STORAGE_KEY = 'quince_identity_config_v1'
/** Avoid stale pool config if backend rotates pools — tune if needed. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000

interface CachedEnvelope {
  savedAt: number
  regionId: number
  emailKey: string
  response: IdentityConfigApiResponse
}

function cacheKey(regionId: number, email: string | undefined): string {
  return `${regionId}:${email ?? ''}`
}

/** Last identity-config resolution from `authConfigDataHolder` / `fetchIdentityConfigForRegion` (network or memory cache). */
let lastIdentityFetch: {
  regionId: number
  emailKey: string
  response: IdentityConfigApiResponse
} | null = null

/**
 * Called after every successful identity-config resolution. Persists to localStorage so the next
 * hard reload can configure Cognito synchronously in _app's render body (before child effects).
 */
export function recordIdentityConfigFetch(
  regionId: number,
  email: string | undefined,
  response: IdentityConfigApiResponse,
): void {
  lastIdentityFetch = { regionId, emailKey: email ?? '', response }
  writeIdentityConfigCache(regionId, email, response)
}

/**
 * If the last fetch (same tab) already resolved identity-config for this `regionId` + email, copy it to localStorage.
 * Returns true when no network fallback is needed.
 */
export function tryPersistLocalStorageFromLastIdentityFetch(regionId: number, email: string): boolean {
  const normalized = email.trim().toLowerCase()
  if (!normalized || !lastIdentityFetch) {
    return false
  }
  if (lastIdentityFetch.regionId !== regionId || lastIdentityFetch.emailKey !== normalized) {
    return false
  }
  writeIdentityConfigCache(regionId, normalized, lastIdentityFetch.response)
  return true
}

function getStorage(): Storage | null {
  if (typeof localStorage === 'undefined') {
    return null
  }
  return localStorage
}

/**
 * Extracts Cognito pool config directly from tokens in localStorage.
 * The JWT idToken contains `iss` (region + poolId) and `aud` (clientId).
 * This is the most reliable source — the token was issued by the user's actual pool.
 */
export function readAuthConfigFromCognitoTokens(): {
  userPoolId: string
  userPoolClientId: string
  region: string
} | null {
  if (typeof window === 'undefined') return null
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.endsWith('.idToken')) continue

      const raw = localStorage.getItem(key)
      if (!raw) continue

      const jwt = typeof raw === 'string' && raw.startsWith('"') ? (JSON.parse(raw) as string) : raw
      const parts = jwt.split('.')
      if (parts.length !== 3 || !parts[1]) continue

      const payload = JSON.parse(atob(parts[1])) as { iss?: string; aud?: string }
      if (!payload.iss || !payload.aud) continue

      // iss = https://cognito-idp.{region}.amazonaws.com/{userPoolId}
      const issUrl = new URL(payload.iss)
      const region = issUrl.hostname.split('.')[1]
      const userPoolId = issUrl.pathname.slice(1) // remove leading /

      if (!region || !userPoolId) continue

      return {
        userPoolId,
        userPoolClientId: payload.aud,
        region,
      }
    }
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      extraInfo: { context: 'READ_AUTH_CONFIG_FROM_COGNITO_TOKENS' },
    })
  }
  return null
}

/**
 * Read whatever identity-config is cached without region/email matching.
 * Used at module scope in _app where pageProps aren't available yet.
 */
export function readRawIdentityConfigCache(): IdentityConfigApiResponse | null {
  const storage = getStorage()
  if (!storage) {
    return null
  }
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as CachedEnvelope
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.response
  } catch {
    return null
  }
}

/**
 * Read cached identity-config API response (localStorage, shared across tabs). Miss if region/email/TTL mismatch.
 */
export function readIdentityConfigCache(regionId: number, email: string | undefined): IdentityConfigApiResponse | null {
  const storage = getStorage()
  if (!storage) {
    return null
  }
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as CachedEnvelope
    if (cacheKey(parsed.regionId, parsed.emailKey) !== cacheKey(regionId, email)) {
      return null
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.response
  } catch {
    return null
  }
}

/**
 * Persist identity-config after successful login only — see `fetchAndPersistIdentityConfigForLogin`.
 */
function writeIdentityConfigCache(
  regionId: number,
  email: string | undefined,
  response: IdentityConfigApiResponse,
): void {
  const storage = getStorage()
  if (!storage) {
    return
  }
  try {
    const envelope: CachedEnvelope = {
      savedAt: Date.now(),
      regionId,
      emailKey: email ?? '',
      response,
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(envelope))
  } catch {
    // Quota / private mode — ignore
  }
}

/**
 * Fetches identity-config by email for the current browse region and stores it in localStorage.
 * Call from the login success path so later `authConfigDataHolder.initialize` can read cache and skip HTTP.
 */
export async function fetchAndPersistIdentityConfigForLogin(regionId: number, email: string): Promise<void> {
  const normalized = email.trim().toLowerCase()
  if (!normalized) {
    return
  }
  try {
    const response = await fetchIdentityConfigByEmail(regionId, normalized)
    writeIdentityConfigCache(regionId, normalized, response)
  } catch (err) {
    captureException(err as Error, {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      extraInfo: { regionId, email: normalized, context: 'IDENTITY_CONFIG_LOGIN_PERSIST' },
    })
  }
}

/** Call on logout so the next user does not reuse another user’s pool config. */
export function clearIdentityConfigCache(): void {
  lastIdentityFetch = null
  const storage = getStorage()
  if (!storage) {
    return
  }
  try {
    storage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
